import { useCallback, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { ApiError } from '@/services/api-client';
import { Stepper, type StepperStep } from '@/components/stepper';
import { LocalePicker } from '@/components/locale-picker';
import { LogoMark } from '@/components/logo-mark';
import { ModulesStep } from './modules-step';
import { DetailsStep, type DetailsFormState } from './details-step';
import { PaymentStep } from './payment-step';
import { ResultStep } from './result-step';

/** Every tenant's trial length — flat, since there's no plan tier to vary it by (matches `DEFAULT_TRIAL_DAYS` server-side). */
const TRIAL_DAYS = 14;

type StepKey = 'modules' | 'details' | 'payment' | 'result';

const EMPTY_DETAILS: DetailsFormState = {
  churchName: '',
  slug: '',
  slugEdited: false,
  ownerName: '',
  email: '',
  password: '',
};

/**
 * Church self-registration, as a four-step wizard.
 *
 * Modeled on Odoo's trial signup (app selection → account → payment →
 * result), adapted to what TABITA actually has: a church builds its own
 * module bundle rather than picking a fixed plan, and there is exactly one
 * API call at the end — `register()` (from `useAuth`) already provisions
 * the tenant, owner, module subscription and core modules atomically. The
 * payment step is cosmetic — see its own doc comment for why — so
 * "approved" and "denied" both come from that one call's outcome, not from
 * a separate charge. A bundle that costs nothing skips the payment step
 * entirely — there's nothing to pay for.
 */
export function RegisterPage(): ReactNode {
  const { register } = useAuth();
  const { t, locale } = useI18n();
  const navigate = useNavigate();

  const [step, setStep] = useState<StepKey>('modules');
  const [moduleKeys, setModuleKeys] = useState<string[]>([]);
  const [totalCents, setTotalCents] = useState(0);
  const [details, setDetails] = useState<DetailsFormState>(EMPTY_DETAILS);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tenantSelected, setTenantSelected] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  /** Keyed by the request field the server rejected (`slug`, `password`, ...). */
  const [serverFieldErrors, setServerFieldErrors] = useState<Record<string, string>>({});

  const steps: StepperStep[] = [
    { key: 'modules', label: t('registration.steps.modules') },
    { key: 'details', label: t('registration.steps.details') },
    { key: 'payment', label: t('registration.steps.payment') },
    { key: 'result', label: t('registration.steps.result') },
  ];

  function handleDetailsChange(patch: Partial<DetailsFormState>): void {
    setDetails((previous) => ({ ...previous, ...patch }));
  }

  const goToApp = useCallback(() => {
    void navigate(tenantSelected ? '/people' : '/select-church', { replace: true });
  }, [navigate, tenantSelected]);

  async function submit(): Promise<void> {
    setIsSubmitting(true);

    try {
      const result = await register({
        churchName: details.churchName,
        slug: details.slug,
        ownerName: details.ownerName,
        email: details.email,
        password: details.password,
        moduleKeys,
        locale,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        currency: 'USD',
      });

      // A brand-new account never carries an MFA requirement, but the return
      // type is shared with `login()` — narrowed the same way it is there.
      if ('mfaRequired' in result) {
        throw new ApiError('INTERNAL_ERROR', 'Unexpected MFA requirement on registration', 500);
      }

      setErrorMessage(null);
      setServerFieldErrors({});
      setTenantSelected(result.tenantSelected);
      setStep('result');
    } catch (caught) {
      const code = caught instanceof ApiError ? caught.code : 'INTERNAL_ERROR';
      // A rejected password-policy check (`assertPasswordMeetsPolicy`) is a
      // domain rule, not a DTO field validation, so it carries `violations`
      // — i18n keys of its own — rather than the `fields` array `fieldErrors`
      // below reads. Translating them beats the generic VALIDATION_FAILED
      // fallback, which would say only "check the highlighted fields" while
      // highlighting nothing.
      const passwordViolations = caught instanceof ApiError ? caught.passwordViolations : [];
      const message =
        passwordViolations.length > 0
          ? passwordViolations.map((violation) => t(violation)).join(' ')
          : t(`errors.${code}`, { fallback: t('errors.INTERNAL_ERROR') });

      // Three sources of field-level detail: a genuine `VALIDATION_FAILED`
      // carries a `fields` array covering any rejected field at once (see
      // `ApiError.fieldErrors`); the two conflict codes below carry no such
      // array — each names exactly one field by virtue of which code it is;
      // a password-policy violation always names `password`.
      const fieldErrors: Record<string, string> =
        caught instanceof ApiError ? caught.fieldErrors : {};
      if (code === 'TENANCY_SLUG_TAKEN') fieldErrors.slug = message;
      if (code === 'IDENTITY_EMAIL_ALREADY_REGISTERED') fieldErrors.email = message;
      if (passwordViolations.length > 0) fieldErrors.password = message;

      // Deliberately no `setStep` here — a failed submission stays on
      // whichever step made it (details, for a free bundle; payment,
      // otherwise) with the error right there, rather than advancing to
      // "Done" and making the user click back into a wizard that looks
      // finished. `result` is reached only by the success path below.
      setErrorMessage(message);
      setServerFieldErrors(fieldErrors);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="auth-page">
      <div className="wizard-card">
        <header className="auth-header">
          <LogoMark size={40} />
          <h1 className="auth-title">{t('registration.title')}</h1>
        </header>

        <Stepper steps={steps} activeKey={step} />

        {step === 'modules' && (
          <ModulesStep
            selectedModuleKeys={moduleKeys}
            onChange={setModuleKeys}
            onNext={(nextTotalCents) => {
              setTotalCents(nextTotalCents);
              setStep('details');
            }}
          />
        )}

        {step === 'details' && (
          <DetailsStep
            value={details}
            onChange={handleDetailsChange}
            onNext={() => {
              // Nothing to charge — skip the payment step and submit directly.
              if (totalCents <= 0) {
                void submit();
                return;
              }
              setStep('payment');
            }}
            onBack={() => setStep('modules')}
            serverFieldErrors={serverFieldErrors}
            formError={errorMessage}
          />
        )}

        {step === 'payment' && (
          <PaymentStep
            trialDays={TRIAL_DAYS}
            isSubmitting={isSubmitting}
            onConfirm={() => void submit()}
            onBack={() => setStep('details')}
            errorMessage={errorMessage}
          />
        )}

        {step === 'result' && <ResultStep churchName={details.churchName} onGoToApp={goToApp} />}

        <footer className="auth-footer">
          <LocalePicker />
        </footer>
      </div>
    </main>
  );
}
